import { memo, useMemo } from 'react';
import {
  Bot,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  MessageCircle,
  Loader2,
  XCircle,
} from 'lucide-react';
import type { AxonEvent } from '@reflex/shared';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import { Timestamp } from '@reflex/ui/components/shared/Timestamp';
import { cn } from '@reflex/ui/lib/utils';

/**
 * Status derived from agent events seen in a stream group.
 */
type DerivedStatus = 'running' | 'completed' | 'error' | 'needs_input' | 'stopped';

interface DerivedStreamSummary {
  agentId: string | null;
  agentName: string;
  status: DerivedStatus;
  summary: string | null;
  errorMessage: string | null;
  eventCount: number;
  firstTimestamp: number | null;
  lastTimestamp: number | null;
}

const STATUS_LABEL: Record<DerivedStatus, string> = {
  running: 'Running',
  completed: 'Completed',
  error: 'Error',
  needs_input: 'Needs input',
  stopped: 'Stopped',
};

const STATUS_CONTAINER_CLASSES: Record<DerivedStatus, string> = {
  running: 'border-status-info/30 bg-status-info/5',
  completed: 'border-status-success/30 bg-status-success/5',
  error: 'border-status-error/30 bg-status-error/5',
  needs_input: 'border-status-warning/30 bg-status-warning/5',
  stopped: 'border-muted-foreground/30 bg-muted/40',
};

const STATUS_BADGE_CLASSES: Record<DerivedStatus, string> = {
  running: 'bg-status-info/15 text-status-info',
  completed: 'bg-status-success/15 text-status-success',
  error: 'bg-status-error/15 text-status-error',
  needs_input: 'bg-status-warning/15 text-status-warning',
  stopped: 'bg-muted text-muted-foreground',
};

const STATUS_ICON: Record<DerivedStatus, typeof Bot> = {
  running: Loader2,
  completed: CheckCircle2,
  error: AlertCircle,
  needs_input: MessageCircle,
  stopped: XCircle,
};

function deriveSummary(events: AxonEvent[]): DerivedStreamSummary {
  let agentId: string | null = null;
  let agentName: string | null = null;
  let summary: string | null = null;
  let errorMessage: string | null = null;
  let hasComplete = false;
  let hasError = false;
  let hasNeedInput = false;
  let hasStopped = false;
  let firstTimestamp: number | null = null;
  let lastTimestamp: number | null = null;

  for (const event of events) {
    if (firstTimestamp == null || event.timestamp < firstTimestamp)
      firstTimestamp = event.timestamp;
    if (lastTimestamp == null || event.timestamp > lastTimestamp) lastTimestamp = event.timestamp;

    const payload = parseEventPayload<{
      agentId?: string;
      name?: string;
      summary?: string;
      error?: string;
      message?: string;
    }>(event.payload);

    if (!agentId && typeof payload?.agentId === 'string') {
      agentId = payload.agentId;
    }

    switch (event.type) {
      case 'agent.started':
        if (typeof payload?.name === 'string') agentName = payload.name;
        break;
      case 'agent.name':
        if (typeof payload?.name === 'string') agentName = payload.name;
        break;
      case 'agent.complete':
        hasComplete = true;
        if (typeof payload?.summary === 'string') summary = payload.summary;
        break;
      case 'agent.error':
        hasError = true;
        if (typeof payload?.error === 'string') {
          errorMessage = payload.error;
        } else if (typeof payload?.message === 'string') {
          errorMessage = payload.message;
        }
        break;
      case 'agent.need_input':
        hasNeedInput = true;
        break;
      case 'agent.stopped':
      case 'agent.killed':
        hasStopped = true;
        break;
      default:
        break;
    }
  }

  // Prefer explicit error over stopped — a kill after an error should stay Error.
  let status: DerivedStatus = 'running';
  if (hasError) status = 'error';
  else if (hasStopped) status = 'stopped';
  else if (hasComplete) status = 'completed';
  else if (hasNeedInput) status = 'needs_input';

  return {
    agentId,
    agentName: agentName ?? 'Agent',
    status,
    summary,
    errorMessage,
    eventCount: events.length,
    firstTimestamp,
    lastTimestamp,
  };
}

interface AgentStreamSummaryBubbleProps {
  /** Stream id used as a stable key and exposed via data-testid. */
  streamId: string;
  /** All Axon events belonging to this stream, in chronological order. */
  events: AxonEvent[];
  /** Opens the agent slide-over for the resolved agentId. */
  onOpenAgent?: (agentId: string) => void;
}

/**
 * Summary card rendered in the flow stream view for a single agent's stream.
 *
 * Replaces the previous behaviour of inlining every individual agent.*
 * bubble — users now click "View Agent" to open the same slide-over that
 * the Timeline tab uses.
 */
export const AgentStreamSummaryBubble = memo(function AgentStreamSummaryBubble({
  streamId,
  events,
  onOpenAgent,
}: AgentStreamSummaryBubbleProps) {
  const derived = useMemo(() => deriveSummary(events), [events]);
  const StatusIcon = STATUS_ICON[derived.status];

  const canOpen = derived.agentId != null && onOpenAgent != null;
  const handleOpen = () => {
    if (derived.agentId && onOpenAgent) onOpenAgent(derived.agentId);
  };

  return (
    <div
      className={cn('rounded-xl border p-4', STATUS_CONTAINER_CLASSES[derived.status])}
      data-testid={`agent-stream-summary-${streamId}`}
      data-status={derived.status}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 rounded-full bg-status-info/15 p-1.5">
          <Bot className="h-4 w-4 text-status-info" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium text-foreground">{derived.agentName}</p>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                STATUS_BADGE_CLASSES[derived.status],
              )}
            >
              <StatusIcon
                className={cn('h-3 w-3', derived.status === 'running' && 'animate-spin')}
              />
              {STATUS_LABEL[derived.status]}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {derived.eventCount} event{derived.eventCount === 1 ? '' : 's'}
            </span>
          </div>

          {derived.errorMessage ? (
            <p className="mt-1.5 whitespace-pre-wrap text-xs text-status-error">
              {derived.errorMessage}
            </p>
          ) : derived.summary ? (
            <p className="mt-1.5 whitespace-pre-wrap text-xs text-foreground">{derived.summary}</p>
          ) : (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Open the agent to see its full conversation and tool calls.
            </p>
          )}

          {canOpen && (
            <button
              type="button"
              onClick={handleOpen}
              aria-label={`View agent ${derived.agentName}`}
              data-testid={`view-agent-${streamId}`}
              className={cn(
                'mt-2 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
                derived.status === 'running'
                  ? 'border-status-info/30 bg-status-info/10 py-1.5 text-status-info hover:bg-status-info/15'
                  : 'border-status-info/20 bg-status-info/5 text-status-info hover:bg-status-info/10',
              )}
            >
              <Bot className={derived.status === 'running' ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
              View Agent
              <ExternalLink className="ml-0.5 h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {derived.firstTimestamp != null && (
        <div className="mt-2 pl-10">
          <Timestamp timestamp={derived.firstTimestamp} />
        </div>
      )}
    </div>
  );
});
